import { useForm } from '@mantine/form';
import { noop } from 'lodash';
import useLoadingAction from 'hooks/use-loading-action';
import { Box, Group, TextInput } from '@mantine/core';
import { FEATURE_TOGGLE_MY_PROFILE_TWO_FACTOR, FEATURE_TOGGLE_USER_NFQES_EMAIL } from 'env';
import createValidator from 'components/forms/validators/create-validator';
import required from 'components/forms/validators/rules/rule-required';
import FormInputGroup from 'components/forms/FormInputGroup';
import emailFormat from 'components/forms/validators/rules/rule-email-format';
import ChangePasswordForm, { ChangePasswordFormData } from 'components/forms/my-profile/ChangePasswordForm';
import TwoFactorAuthenticationForm from 'components/forms/my-profile/TwoFactorAuthenticationForm';
import { FormMessage } from 'components/forms/FormProps';
import FormWrapper from 'components/forms/FormWrapper';
import { IconCheck } from '@tabler/icons-react';
import { useDirtyFormHandler } from 'hooks/use-dirty-form-handler';
import { useResponsiveDesign } from 'hooks/use-responsive-design';
import UploadFile from 'components/files/UploadFile';
import { AuthLoggedUserInfoResponse } from 'api/actions/auth-logged-user-info/auth-logged-user-info-response';
import { AuthTwoFactorConfirmSetupPayload } from 'api/actions/auth-two-factor-confirm-setup/auth-two-factor-confirm-setup-payload';
import { AuthTwoFactorDisablePayload } from 'api/actions/auth-two-factor-disable/auth-two-factor-disable-payload';
import { AuthTwoFactorStartSetupResponse } from 'api/actions/auth-two-factor-start-setup/auth-two-factor-start-setup-response';

/**
 * The data collected from the form.
 */
export interface MyProfileFormData {
  preNominals?: string;
  postNominals?: string;
  firstName: string;
  lastName: string;
  email: string;
  nfqesEmail?: string;
  phoneNumber?: string;
  signatureFileId?: string;
}

/**
 * Parameters of the MyProfileForm component.
 */
export interface MyProfileFormProps {
  initialValues: MyProfileFormData;
  passwordChangeMessage?: FormMessage;
  latestRecoveryCodes?: string[];
  twoFactorState: Pick<
    AuthLoggedUserInfoResponse,
    'twoFactorEnabled' | 'twoFactorSetupPending' | 'twoFactorRequired' | 'twoFactorRecoveryCodesRemaining'
  >;
  onSubmit?: (data: MyProfileFormData) => void;
  onPasswordChange?: (data: ChangePasswordFormData) => void;
  onStartTwoFactorSetup?: () => Promise<AuthTwoFactorStartSetupResponse>;
  onConfirmTwoFactorSetup?: (data: AuthTwoFactorConfirmSetupPayload) => Promise<void>;
  onDisableTwoFactor?: (data: AuthTwoFactorDisablePayload) => Promise<void>;
  onClearLatestRecoveryCodes?: () => void;
}

/**
 * Form for editing user's own profile.
 */
export default function MyProfileForm({
  initialValues,
  passwordChangeMessage,
  latestRecoveryCodes,
  twoFactorState,
  onPasswordChange = noop,
  onSubmit = noop,
  onStartTwoFactorSetup,
  onConfirmTwoFactorSetup,
  onDisableTwoFactor,
  onClearLatestRecoveryCodes,
}: MyProfileFormProps) {
  const { isTablet } = useResponsiveDesign();
  const [{ loading }, submit] = useLoadingAction(onSubmit);

  const form = useForm<MyProfileFormData>({
    initialValues: { nfqesEmail: '', ...initialValues },
    validate: {
      firstName: createValidator([required]),
      lastName: createValidator([required]),
      email: createValidator([required, emailFormat]),
      nfqesEmail: FEATURE_TOGGLE_USER_NFQES_EMAIL
        ? (value) => (value?.trim() ? createValidator([emailFormat])(value.trim()) : null)
        : undefined,
    },
  });

  useDirtyFormHandler(form);

  return (
    <FormWrapper
      onSubmit={form.onSubmit(submit)}
      loading={loading}
      primaryButtonText="Uložiť"
      primaryIcon={<IconCheck stroke="1.5" height={24} width={24} />}
      skipSecondaryButtonConfirm={!form.isDirty()}
    >
      <FormInputGroup groupTitle="Osobné údaje">
        <Group grow spacing={isTablet ? 20 : 40}>
          <TextInput
            label="Titul pred menom"
            size="lg"
            name="objekto-preNominals"
            {...form.getInputProps('preNominals')}
          />
          <TextInput
            label="Titul za menom"
            size="lg"
            name="objekto-postNominals"
            {...form.getInputProps('postNominals')}
          />
        </Group>
        <Group grow spacing={isTablet ? 20 : 40}>
          <TextInput label="Meno" size="lg" name="objekto-firstName" {...form.getInputProps('firstName')} />
          <TextInput label="Priezvisko" size="lg" name="objekto-lastName" {...form.getInputProps('lastName')} />
        </Group>
        <Group grow spacing={isTablet ? 20 : 40}>
          <TextInput
            size="lg"
            type="email"
            placeholder="email@email.com"
            label="E-mail"
            name="objekto-email"
            {...form.getInputProps('email')}
          />
          <TextInput
            label="Telefón"
            placeholder="+421 123 456 789"
            size="lg"
            type="tel"
            name="objekto-phoneNumber"
            {...form.getInputProps('phoneNumber')}
          />
        </Group>
      </FormInputGroup>

      <FormInputGroup groupTitle="Podpis">
        {FEATURE_TOGGLE_USER_NFQES_EMAIL && (
          <TextInput
            label="Mailový účet používaný na službe elektronického podpisu"
            size="lg"
            type="email"
            name="objekto-nfqesEmail"
            {...form.getInputProps('nfqesEmail')}
          />
        )}
        <Box maw={600}>
          <UploadFile
            label=""
            description="Nahrajte súbor s vaším podpisom (formát PNG alebo JPG)"
            accept="image/jpeg,image/png,image/jpg"
            value={form.values.signatureFileId}
            error={form.errors.signatureFileId as string | undefined}
            onChange={(value) => form.setFieldValue('signatureFileId', value)}
          />
        </Box>
      </FormInputGroup>

      <ChangePasswordForm onSubmit={onPasswordChange} message={passwordChangeMessage} />

      {FEATURE_TOGGLE_MY_PROFILE_TWO_FACTOR && (
        <TwoFactorAuthenticationForm
          enabled={twoFactorState.twoFactorEnabled}
          setupPending={twoFactorState.twoFactorSetupPending}
          requiredByPolicy={twoFactorState.twoFactorRequired}
          latestRecoveryCodes={latestRecoveryCodes}
          onClearLatestRecoveryCodes={onClearLatestRecoveryCodes}
          onStartSetup={onStartTwoFactorSetup}
          onConfirmSetup={onConfirmTwoFactorSetup}
          onDisable={onDisableTwoFactor}
        />
      )}
    </FormWrapper>
  );
}
